import React, { createContext, useContext, useEffect, useMemo, useRef } from "react";
import useSound from "use-sound";

import clickSFX from "./assets/sounds/button_medium.mp3";
import softSFX from "./assets/sounds/button_soft.mp3";
import tickSFX from "./assets/sounds/tick.mp3";
import popSFX from "./assets/sounds/pop.mp3";
import successSFX from "./assets/sounds/success.mp3";
import failSFX from "./assets/sounds/fail.mp3";

// Interface sounds. The whole point is that they stay discreet: short, low
// volume, and never two at once for the same gesture. Clicks are handled
// globally here (one listener on the document) rather than button by button —
// so any new button in the app is covered without touching it.

const VOLUME = {
  click: 0.35,   // primary action (validate, play, submit)
  soft: 0.22,    // ordinary click (menu, tab, checkbox, link)
  type: 0.12,    // keystroke in a text field
  tick: 0.25,    // countdown, last seconds
  pop: 0.3,      // a player arrives, someone buzzes
  success: 0.35,
  fail: 0.28,
};

// Elements that make a sound when pressed; `.btn-primary` and submit buttons
// get the fuller click, everything else the soft one.
const PRESSABLE = [
  "button", ".btn", "[role=button]", "[role=menuitem]", "[role=option]",
  "[role=tab]", "a[href]", "label", "summary", "select",
  "input[type=checkbox]", "input[type=radio]", "input[type=file]",
].join(",");
const STRONG = ".btn-primary, button[type=submit]";
// Password fields stay silent on purpose.
const TYPABLE = "input:not([type]), input[type=text], input[type=search], input[type=email], input[type=number], textarea";

const TYPE_THROTTLE_MS = 60;

const noop = () => {};
const SILENT = {
  playClick: noop, playSoft: noop, playType: noop,
  playTick: noop, playPop: noop, playSuccess: noop, playFail: noop,
};

const SoundContext = createContext(SILENT);

// Sounds are available everywhere without prop drilling.
export function useSounds() {
  return useContext(SoundContext);
}

export function SoundProvider({ children }) {
  const [playClick] = useSound(clickSFX, { volume: VOLUME.click, interrupt: true });
  const [playSoft] = useSound(softSFX, { volume: VOLUME.soft, interrupt: true });
  const [playType] = useSound(softSFX, { volume: VOLUME.type, interrupt: true });
  const [playTick] = useSound(tickSFX, { volume: VOLUME.tick, interrupt: true });
  const [playPop] = useSound(popSFX, { volume: VOLUME.pop });
  const [playSuccess] = useSound(successSFX, { volume: VOLUME.success });
  const [playFail] = useSound(failSFX, { volume: VOLUME.fail });

  // `use-sound` hands back a new play function every time a sample finishes
  // loading. The context exposes stable wrappers instead, so a socket handler
  // captured once (inside a useEffect) never holds on to a dead function.
  const ref = useRef(null);
  ref.current = { playClick, playSoft, playType, playTick, playPop, playSuccess, playFail };

  const sounds = useMemo(() => {
    const bind = (name) => (opts) => ref.current[name](opts);
    return {
      playClick: bind("playClick"), playSoft: bind("playSoft"), playType: bind("playType"),
      playTick: bind("playTick"), playPop: bind("playPop"),
      playSuccess: bind("playSuccess"), playFail: bind("playFail"),
    };
  }, []);

  useEffect(() => {
    const muted = (el) => el.closest("[data-no-sfx]") || el.disabled;

    const press = (target) => {
      const el = target.closest && target.closest(PRESSABLE);
      if (!el || muted(el)) return;
      (el.matches(STRONG) ? ref.current.playClick : ref.current.playSoft)();
    };

    // Pointer press rather than click: the sound lands with the finger.
    const onPointerDown = (e) => { if (e.button === 0 || e.pointerType !== "mouse") press(e.target); };
    // Keyboard activation produces no pointer event.
    const onKeyDown = (e) => { if (e.key === "Enter" || e.key === " ") press(e.target); };

    let lastType = 0;
    const onInput = (e) => {
      const el = e.target;
      if (!el.matches || !el.matches(TYPABLE) || muted(el)) return;
      const now = Date.now();
      if (now - lastType < TYPE_THROTTLE_MS) return;
      lastType = now;
      ref.current.playType();
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("input", onInput, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("input", onInput, true);
    };
  }, []);

  return <SoundContext.Provider value={sounds}>{children}</SoundContext.Provider>;
}

// Ticks over the last seconds of a countdown, one sound per second, the very
// last one a touch higher. Silent while `seconds` grows (new question) so a
// restarting timer never re-plays the run-out.
export function useCountdownTick(seconds, { active = true, from = 5 } = {}) {
  const { playTick } = useSounds();
  const previous = useRef(null);

  useEffect(() => {
    if (!active || seconds == null) { previous.current = null; return; }
    const prev = previous.current;
    previous.current = seconds;
    if (prev === null || seconds >= prev) return; // first read, or timer reset
    if (seconds > from || seconds < 0) return;
    playTick({ playbackRate: seconds === 0 ? 1.3 : 1 });
  }, [seconds, active, from]); // eslint-disable-line react-hooks/exhaustive-deps
}

// Plays a sound once when `value` becomes true (and again after it goes back
// to false) — reveals, eliminations, end of round…
export function useSoundOn(value, play) {
  const previous = useRef(false);
  useEffect(() => {
    if (value && !previous.current) play();
    previous.current = !!value;
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
}
